import React, { useState } from 'react';
import { FileText, Download, ExternalLink, Copy, Check } from 'lucide-react';

interface DocumentPreviewProps {
  asset: {
    id: number;
    file_name: string;
    content?: string;
    metadata?: any;
    asset_type: string;
  };
  apiBaseUrl: string;
  isEditing?: boolean;
  onSave?: (content: string) => void;
}

export default function DocumentPreview({ asset, apiBaseUrl, isEditing = false, onSave }: DocumentPreviewProps) {
  const [copied, setCopied] = useState(false);
  const [editedContent, setEditedContent] = useState(asset.content || '');

  // Check if metadata contains an external URL
  const getAssetUrl = () => {
    if (asset.metadata) {
      const meta = typeof asset.metadata === 'string' ? JSON.parse(asset.metadata) : asset.metadata;
      if (meta.url) {
        return meta.url;
      }
    }
    return `${apiBaseUrl}/assets/${asset.id}/download`;
  };

  const assetUrl = getAssetUrl();

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = assetUrl;
    link.download = asset.file_name;
    link.click();
  };

  const handleCopyContent = () => {
    if (asset.content) {
      navigator.clipboard.writeText(asset.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(assetUrl, '_blank');
  };

  const handleSave = () => {
    if (onSave) {
      onSave(editedContent);
    }
  };

  const isPDF = asset.file_name?.toLowerCase().endsWith('.pdf');
  const isJSON = asset.file_name?.toLowerCase().endsWith('.json');
  const isHTML = asset.file_name?.toLowerCase().endsWith('.html');

  // For PDFs, use iframe
  if (isPDF) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-red-600" />
            <span className="font-medium">{asset.file_name}</span>
            {asset.metadata?.pages && (
              <span className="text-sm text-gray-500">({asset.metadata.pages} pages)</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleOpenInNewTab}
              className="p-2 hover:bg-gray-100 rounded"
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Download</span>
            </button>
          </div>
        </div>
        <div className="flex-1">
          <iframe
            src={assetUrl}
            className="w-full h-full border-0"
            title={asset.file_name}
          />
        </div>
      </div>
    );
  }

  // For JSON, format it nicely
  if (isJSON && asset.content) {
    try {
      const jsonContent = typeof asset.content === 'string'
        ? JSON.parse(asset.content)
        : asset.content;
      const formattedJson = JSON.stringify(jsonContent, null, 2);

      return (
        <div className="h-full flex flex-col bg-gray-50">
          <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-green-600" />
              <span className="font-medium">{asset.file_name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCopyContent}
                className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Download className="w-4 h-4" />
                <span className="text-sm">Download</span>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4">
            <pre className="bg-white rounded-lg p-4 text-sm font-mono">
              <code className="language-json">{formattedJson}</code>
            </pre>
          </div>
        </div>
      );
    } catch (e) {
      // If JSON parsing fails, fall through to text display
    }
  }

  // For HTML, render it
  if (isHTML && asset.content) {
    return (
      <div className="h-full flex flex-col bg-gray-50">
        <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-orange-600" />
            <span className="font-medium">{asset.file_name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCopyContent}
              className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'Copied!' : 'Copy HTML'}</span>
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Download</span>
            </button>
          </div>
        </div>
        <div className="flex-1 flex">
          {/* HTML Preview */}
          <div className="flex-1 p-4 bg-white">
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: asset.content }} />
          </div>
          {/* HTML Source */}
          <div className="w-1/2 border-l">
            <div className="bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700">HTML Source</div>
            <div className="overflow-auto p-4 bg-gray-50">
              <pre className="text-xs font-mono">
                <code>{asset.content}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default text document display
  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="bg-white border-b px-4 py-2 pr-64 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FileText className="w-5 h-5 text-gray-600" />
          <span className="font-medium">{asset.file_name}</span>
          {asset.metadata?.size && (
            <span className="text-sm text-gray-500">({asset.metadata.size})</span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {asset.content && (
            <button
              onClick={handleCopyContent}
              className="flex items-center space-x-1 px-3 py-1.5 hover:bg-gray-100 rounded"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
            </button>
          )}
          {isEditing && onSave && (
            <button
              onClick={handleSave}
              className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              Save Changes
            </button>
          )}
          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <Download className="w-4 h-4" />
            <span className="text-sm">Download</span>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {isEditing ? (
          <textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className="w-full h-full p-4 bg-white rounded-lg border font-mono text-sm"
            placeholder="Enter document content..."
          />
        ) : (
          <div className="bg-white rounded-lg p-4">
            <pre className="whitespace-pre-wrap font-mono text-sm">
              {asset.content || 'No content available. Click download to get the file.'}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}